import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { hasFeatureAccess, shouldShowPreview } from '../../config/featureAccess';
import { useSubscription } from '../../hooks/useSubscription';

const MobileTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { features, tenantSlug } = useTenant();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Get user's subscription tier
  const { tier } = useSubscription();
  const userTier = tier || profile?.subscription_tier || 'free';

  // Temporarily hide the mobile tab bar to free space for the chat bubble and use the new top-left menu instead
  const globallyHidden = true;
  const hideForUgc = location?.pathname?.startsWith('/ugc') || location?.pathname?.startsWith('/app/ugc');
  if (globallyHidden || hideForUgc) {
    return null;
  }

  const primaryItems = [
    { path: '/app/dashboard', label: 'Home', icon: 'Home' },
    { path: '/app/daily-drill', label: 'Practice', icon: 'Target' },
    { path: '/app/objection-dojo', label: 'Train', icon: 'Shield' },
    { path: '/app/mega-trainer', label: 'Mega', icon: 'BookOpen' },
  ];

  const secondaryItems = [
    { path: '/app/gamification', label: 'Achievements', icon: 'Zap' },
    { path: '/app/ai-roleplay', label: 'AI Roleplay', icon: 'Users' },
    { path: '/app/conversation-analyzer', label: 'Analyzer', icon: 'MessageSquare' },
    { path: '/app/script-lab', label: 'Scripts', icon: 'FileText' },
    { path: '/app/lumo-progress', label: 'Lumo Progress', icon: 'TrendingUp' },
    { path: '/app/cascade-challenge', label: 'Cascade Challenge', icon: 'Zap' },
    { path: '/app/activity-report', label: 'Activity', icon: 'BarChart' },
    { path: '/app/settings', label: 'Settings', icon: 'Settings' },
  ];

  // Filter items based on access AND tenant features
  const getFilteredItems = (items) => {
    return items.filter(item => {
      const hasAccess = hasFeatureAccess(item.path, userTier);
      const showPreview = shouldShowPreview(item.path);
      
      // Check tenant feature gating (only for non-default tenants)
      let isTenantFeatureEnabled = true;
      if (features && tenantSlug !== 'default') {
        const featureMap = {
          '/app/daily-drill': 'enableObjectionDrills',
          '/app/objection-dojo': 'enableObjectionDrills',
          '/app/script-lab': 'enableScripts',
          '/app/ai-roleplay': 'enableRoleplay',
          '/app/conversation-analyzer': 'enableConversationAnalyzer',
          '/app/gamification': 'enableGamification',
          '/app/cascade-challenge': 'enableGamification',
          '/app/leaderboards': 'enableLeaderboards',
          '/app/mega-trainer': 'enableMegaTrainer',
          '/app/lumo-progress': 'enableLiveCoaching',
          '/app/live-call': 'enableLiveCoaching',
          '/app/phone-sequence': 'enablePhoneSequences',
          '/app/micro-lessons': 'enableMicroLessons',
          '/app/ugc': 'enableUGC'
        };
        
        const requiredFeature = featureMap[item.path];
        if (requiredFeature && !features[requiredFeature]) {
          isTenantFeatureEnabled = false;
        }
      }
      
      return (hasAccess || showPreview) && isTenantFeatureEnabled;
    });
  };

  const filteredPrimaryItems = getFilteredItems(primaryItems);
  const filteredSecondaryItems = getFilteredItems(secondaryItems);

  const isActive = (path) => location?.pathname === path;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setShowMenu(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuItemClick = (path) => {
    const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    navigate(`${path}${tenantQuery}`);
    setShowMenu(false);
  };

  return (
    <nav className="mobile-tab-bar md:hidden">
      <div className="mobile-tab-bar-container">
        {filteredPrimaryItems?.map((item) => {
          const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
          return (
            <Link
              key={item?.path}
              to={`${item?.path}${tenantQuery}`}
              className={`mobile-tab-bar-item ${isActive(item?.path) ? 'active' : ''}`}
            >
              <Icon name={item?.icon} size={20} />
              <span className="mobile-tab-bar-label">{item?.label}</span>
            </Link>
          );
        })}

        {/* More Menu */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className={`mobile-tab-bar-item ${showMenu ? 'active' : ''}`}
          >
            <Icon name="MoreVertical" size={20} />
            <span className="mobile-tab-bar-label">More</span>
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
              {filteredSecondaryItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleMenuItemClick(item?.path)}
                  className={`w-full px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 border-b border-border ${
                    isActive(item?.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-accent'
                  }`}
                >
                  <Icon name={item?.icon} size={16} />
                  {item?.label}
                </button>
              ))}
              <button
                onClick={async () => {
                  const { supabase } = await import('../../lib/supabase');
                  await supabase.auth.signOut();
                  navigate('/authentication');
                }}
                className="w-full px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 text-destructive hover:bg-destructive/10"
              >
                <Icon name="LogOut" size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default MobileTabBar;
